import { BUTTON, type InputBits } from "../sim";

/** Запасная клавиатура: две раскладки на одной клавиатуре. */
export interface KeyLayout {
  readonly name: string;
  readonly keys: Readonly<Record<keyof typeof BUTTON, string>>;
  readonly confirm: string;
}

export const KEY_LAYOUTS: readonly KeyLayout[] = [
  {
    name: "WASD · U I J K · блок O",
    keys: {
      up: "KeyW",
      down: "KeyS",
      left: "KeyA",
      right: "KeyD",
      lightPunch: "KeyU",
      heavyPunch: "KeyI",
      lightKick: "KeyJ",
      heavyKick: "KeyK",
      block: "KeyO",
      enhance: "KeyP",
      super: "KeyL",
      taunt: "KeyT",
      grab: "KeyH",
    },
    confirm: "Space",
  },
  {
    name: "стрелки · Num 4 5 1 2 · блок Num 6",
    keys: {
      up: "ArrowUp",
      down: "ArrowDown",
      left: "ArrowLeft",
      right: "ArrowRight",
      lightPunch: "Numpad4",
      heavyPunch: "Numpad5",
      lightKick: "Numpad1",
      heavyKick: "Numpad2",
      block: "Numpad6",
      enhance: "Numpad3",
      super: "Numpad0",
      taunt: "Numpad7",
      grab: "Numpad9",
    },
    confirm: "Enter",
  },
];

/**
 * Состояние клавиатуры. Нажатие запоминается до ближайшего опроса: даже касание короче кадра не теряется.
 * Клавиши — по физическому месту (event.code), раскладка (русская, английская) не важна.
 */
export class KeyboardState {
  private readonly down = new Set<string>();
  private readonly latched = new Set<string>();
  private focused = true;
  /** Последняя клавиша, дошедшая до страницы (код и как её назвала раскладка) — для индикатора нажатий. */
  lastKey = "";

  constructor(target: Window) {
    target.addEventListener("keydown", (event) => {
      this.lastKey = `${event.code || "без кода"} «${event.key}»`;
      this.down.add(event.code);
      this.latched.add(event.code);
      if (event.code.startsWith("Arrow") || event.code === "Space") event.preventDefault();
    });
    target.addEventListener("keyup", (event) => this.down.delete(event.code));
    target.addEventListener("blur", () => {
      this.down.clear();
      this.focused = false;
    });
    target.addEventListener("focus", () => {
      this.focused = true;
    });
    const page = target.document as Document | undefined;
    this.focused = page ? page.hasFocus() : true;
  }

  /** Есть ли у страницы фокус: без него клавиши уходят в другое окно. */
  get hasFocus(): boolean {
    return this.focused;
  }

  /** Конец опроса кадра: запомненные касания прочитаны. */
  endPoll(): void {
    this.latched.clear();
  }

  isDown(code: string): boolean {
    return this.down.has(code) || this.latched.has(code);
  }

  bits(layout: KeyLayout): InputBits {
    let bits = 0;
    for (const [name, code] of Object.entries(layout.keys))
      if (this.isDown(code)) bits |= BUTTON[name as keyof typeof BUTTON];
    return bits;
  }
}
